// Renders a structured Insight Document (generative UI) returned by the backend
// Visualizer. Each block names a built-in component; LLM-authored components
// (Phase 2) are rendered in a sandboxed iframe. Unknown components degrade
// gracefully to prose/text so a bad block never blanks the answer.

import React, { Suspense, useMemo } from 'react';
import { InsightDocument as InsightDoc, InsightBlock } from './types';
import { resolveTheme } from './theme';
import {
  ShortAnswer, Prose, StatGrid, ThemeBars, QuoteCard, QuoteGroup, Callout,
  ComparisonTable, BulletList,
} from './blocks';
import SegmentBarChart from './SegmentBarChart';

// Lazy so React-UMD + Babel (only needed for authored components) stay out of the
// main bundle.
const SandboxedComponent = React.lazy(() => import('./SandboxedComponent'));

// Registry: component name (matches backend visual_components.py) -> React component.
// Props are heterogeneous per component and validated at the block level (and
// grounded server-side), so a loose prop type is intentional for the map.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REGISTRY: Record<string, React.FC<any>> = {
  ShortAnswer,
  Prose,
  StatGrid,
  ThemeBars,
  QuoteCard,
  QuoteGroup,
  Callout,
  SegmentBarChart,
  ComparisonTable,
  BulletList,
};

const renderBlock = (
  block: InsightBlock,
  key: React.Key,
  authored: Map<string, string>,
  theme: InsightDoc['theme'],
): React.ReactNode => {
  if (!block || typeof block.component !== 'string') return null;
  const props = (block.props || {}) as Record<string, unknown>;

  // LLM-authored component → sandboxed iframe. A built-in name always resolves to
  // the trusted built-in component — an authored component can never shadow it
  // (defense-in-depth; the backend already excludes built-in names from authoring).
  const isBuiltin = block.component in REGISTRY;
  const source = isBuiltin ? undefined : authored.get(block.component);
  if (source) {
    return (
      <Suspense key={key} fallback={null}>
        <SandboxedComponent name={block.component} source={source} props={props} theme={theme} />
      </Suspense>
    );
  }

  const Comp = REGISTRY[block.component];
  if (Comp) return <Comp key={key} {...props} style={block.style} />;
  // Defensive fallback for an unknown component name.
  const text = (props.markdown || props.text) as string | undefined;
  return text ? <Prose key={key} markdown={text} /> : null;
};

const InsightDocument: React.FC<{ doc: InsightDoc }> = ({ doc }) => {
  const authored = useMemo(() => {
    const map = new Map<string, string>();
    (doc?.components_authored || []).forEach((c) => {
      if (c?.name && c?.source_tsx) map.set(c.name, c.source_tsx);
    });
    return map;
  }, [doc]);

  // Resolve the (possibly AI-set, possibly unknown) theme to CSS variables that
  // cascade to every block via the .insight-root wrapper.
  const themeVars = useMemo(() => resolveTheme(doc?.theme), [doc]);

  if (!doc || !Array.isArray(doc.sections) || doc.sections.length === 0) return null;
  return (
    <div className="insight-root max-w-none" style={themeVars}>
      {doc.sections.map((section, si) => (
        <section key={si} className={si > 0 ? 'mt-6' : ''}>
          {section.heading && (
            <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--insight-ink)' }}>{section.heading}</h2>
          )}
          {(section.blocks || []).map((block, bi) => renderBlock(block, `${si}-${bi}`, authored, doc.theme))}
        </section>
      ))}
    </div>
  );
};

export default InsightDocument;
